import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { isValidPhoneNumber, getCountries } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input";
import { Loader2 } from "lucide-react";
import CustomDropdown from "../../components/common/CustomDropdown.jsx";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown.jsx";
import { languages } from "../../utils/languages.js";
import host from "../../global.jsx";
import { candidateAuthHeader } from "../../utils/candidateAuth";
import { useTheme } from "../../hooks/useTheme.js";

export default function CandidateFormPage() {
  const { interview_id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const isWhiteLabelingEnable = true; // or pull from redux like TestHomePage

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [defaultCountry, setDefaultCountry] = useState("IN");

  // Step 1
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [age, setAge] = useState("");

  // Step 2
  const [currentCompany, setCurrentCompany] = useState("");
  const [designation, setDesignation] = useState("");
  const [experience, setExperience] = useState("");
  const [lastSalary, setLastSalary] = useState("");
  const [salaryExpectation, setSalaryExpectation] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("");
  const [expectedJoining, setExpectedJoining] = useState("");

  // Step 3
  const [gender, setGender] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [languagesKnown, setLanguagesKnown] = useState("");
  const [collegeYears, setCollegeYears] = useState("");
  const [visaStatus, setVisaStatus] = useState("");
  const [otherVisaDetails, setOtherVisaDetails] = useState("");
  const [currentBenefits, setCurrentBenefits] = useState([]);
  const [relativeName, setRelativeName] = useState("");
  const [relativeDesignation, setRelativeDesignation] = useState("");
  const [sourceOfApplication, setSourceOfApplication] = useState("");
  const [otherSourceDetails, setOtherSourceDetails] = useState("");
  const [referralName, setReferralName] = useState("");
  const [referralDesignation, setReferralDesignation] = useState("");

  // Same options as CustomATSForm
  const experienceOptions = ["0-1 years","1-2 years","2-3 years","3-5 years","5-7 years","7-10 years","10+ years"];
  const noticePeriodOptions = ["Fresher","Immediate","15 days","30 days","45 days","60 days","90 days","Serving notice period"];
  const expectedJoiningDateOptions = ["Immediate","Less than 1 month","More than 1 month"];
  const genderOptions = ["Male","Female","Other"];
  const maritalStatusOptions = ["Single","Married","Divorced"];
  const visaStatusOptions = ["Visit Visa","Spouse Visa","Employment Visa","Cancelled Visa","Over Stay","Parental Visa","Golden Visa"];
  const collegeYearsOptions = ["High School","1 Year","2 Years","3 Years","4 Years","4+ Years","Master's Degree"];
  const benefitsOptions = ["All","Medical Insurance","Family Air Ticket","Yearly Bonus"];
  const sourceOptions = ["LinkedIn","Naukri.com","Indeed","Monster.com","Company Website","Employee Referral","Recruiter Agency","Social Media"];

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const { data } = await axios.get("https://ipwho.is/", { timeout: 3000 });
        const code = data.country_code?.toUpperCase();
        if (code && getCountries().includes(code)) setDefaultCountry(code);
      } catch (e) {}
    };
    detectCountry();
  }, []);

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!name.trim()) newErrors.name = "Full name is required";
      if (!email.trim()) newErrors.email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Enter a valid email";
      if (!mobile || !isValidPhoneNumber(mobile)) newErrors.mobile = "Valid mobile number is required";
      if (!city.trim()) newErrors.city = "City is required";
      if (!state.trim()) newErrors.state = "State is required";
      if (!age) newErrors.age = "Age is required";
      else if (parseInt(age) < 0 || parseInt(age) > 100) newErrors.age = "Age must be between 0 and 100";
    }
    if (step === 2) {
      if (!currentCompany.trim()) newErrors.currentCompany = "Current company is required";
      if (!designation.trim()) newErrors.designation = "Designation is required";
      if (!experience) newErrors.experience = "Experience is required";
      if (!lastSalary.trim()) newErrors.lastSalary = "Current salary is required";
      if (!salaryExpectation.trim()) newErrors.salaryExpectation = "Salary expectation is required";
      if (!noticePeriod) newErrors.noticePeriod = "Notice period is required";
      if (!expectedJoining) newErrors.expectedJoining = "Expected joining date is required";
    }
    if (step === 3) {
      if (!gender) newErrors.gender = "Gender is required";
      if (!languagesKnown.trim()) newErrors.languagesKnown = "Languages known is required";
      if (!visaStatus) newErrors.visaStatus = "Visa status is required";
      if (!sourceOfApplication) newErrors.sourceOfApplication = "Source of application is required";
      if (sourceOfApplication === "Employee Referral") {
        if (!referralName.trim()) newErrors.referralName = "Employee name is required";
        if (!referralDesignation.trim()) newErrors.referralDesignation = "Employee designation is required";
      }
    }
    return newErrors;
  };

  const nextStep = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setErrors({});
    setCurrentStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prevStep = () => {
    setCurrentStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    // Validate all steps before submit
    for (let step = 1; step <= 3; step++) {
      const stepErrors = validateStep(step);
      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setCurrentStep(step);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const finalVisaStatus = visaStatus === "Other" ? otherVisaDetails.trim() : visaStatus;
      const finalOtherSource = sourceOfApplication === "Employee Referral"
        ? `${referralName.trim()} - ${referralDesignation.trim()}`
        : otherSourceDetails.trim();

      await axios.post(`${host}/candidate/${interview_id}/submit_candidate_form`, {
        name: name.trim(),
        email: email.trim(),
        mobile,
        city: city.trim(),
        state: state.trim(),
        age: parseInt(age),
        current_company: currentCompany.trim(),
        designation: designation.trim(),
        experience,
        last_salary: lastSalary.trim(),
        salary_expectation: salaryExpectation.trim(),
        notice_period: noticePeriod,
        expected_joining: expectedJoining,
        gender,
        marital_status: maritalStatus,
        languages_known: languagesKnown,
        college_years: collegeYears,
        visa_status: finalVisaStatus,
        current_benefits: currentBenefits.join(", "),
        relatives_in_company: relativeName.trim() && relativeDesignation.trim()
          ? `${relativeName.trim()} - ${relativeDesignation.trim()}`
          : "",
        source_of_application: sourceOfApplication,
        other_source_details: finalOtherSource,
      }, { headers: candidateAuthHeader(interview_id) });
      toast.success("Form saved successfully");
      setTimeout(() => {
        navigate(`/candidate/${interview_id}/testhomepage`);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-transparent border ${errors[field] ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`;

  const labelClass = `block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`;

  return (
    <div 
      className={`min-h-screen px-4 py-10 ${theme === "light" ? "bg-[#FAF9F6]" : ""}`}
      style={theme === "light" ? { scrollbarColor: "#cbd5e1 #f1f5f9" } : {}}
    >
      <div className="w-11/12 mx-auto">

        {/* Header */}
        <div className={`rounded-3xl p-8 mb-6 ${theme === "light" ? "bg-white shadow-sm border border-gray-100" : "bg-[#16161f] border border-white/10"}`}>
          <h2 className={`text-2xl font-medium ${theme === "light" ? "text-slate-900" : "text-white"}`}>
            Candidate Information
          </h2>
          <p className={`mt-1 text-sm ${theme === "light" ? "text-gray-500" : "text-gray-400"}`}>
            Please fill in your details before proceeding to the assessments.
          </p>
          {/* Step Progress */}
          <div className="relative mt-8 mb-2">
            <div className={`absolute h-[2px] top-4 left-0 right-0 ${theme === "light" ? "bg-gray-200" : "bg-gray-700"} rounded-full overflow-hidden`}>
              <div
                className="h-[2px] bg-gradient-to-r from-pink-500 to-blue-500 transition-all duration-500 rounded-full"
                style={{ width: currentStep === 1 ? "25%" : currentStep === 2 ? "70%" : "100%" }}
              />
            </div>
            <div className="relative flex justify-between items-start">
              {[
                { step: 1, label: "Personal Information" },
                { step: 2, label: "Work Experience" },
                { step: 3, label: "Additional Information" },
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center z-10">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all duration-300 ${
                    currentStep > item.step
                      ? "border-green-500 text-green-600 bg-white"
                      : currentStep === item.step
                      ? "border-pink-500 text-pink-600 bg-white"
                      : theme === "light" ? "border-gray-300 text-gray-400 bg-white" : "border-gray-600 text-gray-500 bg-[#16161f]"
                  }`}>
                    {item.step}
                  </div>
                  <p className={`mt-2 text-xs font-medium text-center max-w-[80px] ${
                    currentStep > item.step ? "text-green-500"
                    : currentStep === item.step ? theme === "light" ? "text-slate-800" : "text-white"
                    : theme === "light" ? "text-gray-400" : "text-gray-500"
                  }`}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className={`rounded-3xl p-8 ${theme === "light" ? "bg-white shadow-sm border border-gray-100" : "bg-[#16161f] border border-white/10"}`}>

          {/* STEP 1 */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <h4 className={`text-md font-medium border-b pb-2 ${theme === "light" ? "border-gray-200 text-gray-600" : "border-white/10 text-white"}`}>
                Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                  <input type="text" value={name} onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }} className={inputClass("name")} placeholder="Enter your full name" />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className={labelClass}>Email Address <span className="text-red-500">*</span></label>
                  <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }} className={inputClass("email")} placeholder="your.email@example.com" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className={labelClass}>Mobile Number <span className="text-red-500">*</span></label>
                  <PhoneInput
                    international
                    defaultCountry={defaultCountry}
                    value={mobile}
                    onChange={(val) => { setMobile(val || ""); setErrors(p => ({ ...p, mobile: undefined })); }}
                    className={`w-full px-4 py-0 bg-transparent border ${errors.mobile ? "border-red-500" : "border-gray-400"} rounded-lg focus:outline-none focus:border-[#C1A0D0] transition-colors ${theme === "light" ? "text-gray-700" : "text-gray-300"}`}
                  />
                  {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>City <span className="text-red-500">*</span></label>
                  <input type="text" value={city} onChange={(e) => { setCity(e.target.value); setErrors(p => ({ ...p, city: undefined })); }} className={inputClass("city")} placeholder="Enter your city" />
                  {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
                <div>
                  <label className={labelClass}>State <span className="text-red-500">*</span></label>
                  <input type="text" value={state} onChange={(e) => { setState(e.target.value); setErrors(p => ({ ...p, state: undefined })); }} className={inputClass("state")} placeholder="Enter your state" />
                  {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                </div>
                <div>
                  <label className={labelClass}>Age <span className="text-red-500">*</span></label>
                  <input type="number" value={age} onChange={(e) => { setAge(e.target.value); setErrors(p => ({ ...p, age: undefined })); }} min="0" max="100" className={inputClass("age")} placeholder="Enter your age" />
                  {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button type="button" onClick={nextStep} className="button">Next</button>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <h4 className={`text-md font-medium border-b pb-2 ${theme === "light" ? "border-gray-200 text-gray-600" : "border-white/10 text-white"}`}>
                Work Experience
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Current Company <span className="text-red-500">*</span></label>
                  <input type="text" value={currentCompany} onChange={(e) => { setCurrentCompany(e.target.value); setErrors(p => ({ ...p, currentCompany: undefined })); }} className={inputClass("currentCompany")} placeholder="Enter company name" />
                  {errors.currentCompany && <p className="text-red-500 text-xs mt-1">{errors.currentCompany}</p>}
                </div>
                <div>
                  <label className={labelClass}>Designation <span className="text-red-500">*</span></label>
                  <input type="text" value={designation} onChange={(e) => { setDesignation(e.target.value); setErrors(p => ({ ...p, designation: undefined })); }} className={inputClass("designation")} placeholder="Enter your job title" />
                  {errors.designation && <p className="text-red-500 text-xs mt-1">{errors.designation}</p>}
                </div>
                <div>
                  <label className={labelClass}>Years of Experience <span className="text-red-500">*</span></label>
                  <CustomDropdown options={experienceOptions} value={experience} onChange={(val) => { setExperience(val); setErrors(p => ({ ...p, experience: undefined })); }} placeholder="Select experience" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.experience} />
                  {errors.experience && <p className="text-red-500 text-xs mt-1">{errors.experience}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Current Salary Per Annum <span className="text-red-500">*</span></label>
                  <input type="text" value={lastSalary} onChange={(e) => { setLastSalary(e.target.value); setErrors(p => ({ ...p, lastSalary: undefined })); }} className={inputClass("lastSalary")} placeholder="e.g., 45000 AED" />
                  {errors.lastSalary && <p className="text-red-500 text-xs mt-1">{errors.lastSalary}</p>}
                </div>
                <div>
                  <label className={labelClass}>Salary Expectation Per Annum <span className="text-red-500">*</span></label>
                  <input type="text" value={salaryExpectation} onChange={(e) => { setSalaryExpectation(e.target.value); setErrors(p => ({ ...p, salaryExpectation: undefined })); }} className={inputClass("salaryExpectation")} placeholder="e.g., 50000-60000 AED" />
                  {errors.salaryExpectation && <p className="text-red-500 text-xs mt-1">{errors.salaryExpectation}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Notice Period <span className="text-red-500">*</span></label>
                  <CustomDropdown options={noticePeriodOptions} value={noticePeriod} onChange={(val) => { setNoticePeriod(val); setErrors(p => ({ ...p, noticePeriod: undefined })); }} placeholder="Select notice period" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.noticePeriod} />
                  {errors.noticePeriod && <p className="text-red-500 text-xs mt-1">{errors.noticePeriod}</p>}
                </div>
                <div>
                  <label className={labelClass}>Expected Joining Date <span className="text-red-500">*</span></label>
                  <CustomDropdown options={expectedJoiningDateOptions} value={expectedJoining} onChange={(val) => { setExpectedJoining(val); setErrors(p => ({ ...p, expectedJoining: undefined })); }} placeholder="Select expected joining" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.expectedJoining} />
                  {errors.expectedJoining && <p className="text-red-500 text-xs mt-1">{errors.expectedJoining}</p>}
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={prevStep} className="border-btn !text-gray-600">Back</button>
                <button type="button" onClick={nextStep} className="button">Next</button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <h4 className={`text-md font-medium border-b pb-2 ${theme === "light" ? "border-gray-200 text-gray-600" : "border-white/10 text-white"}`}>
                Additional Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Gender <span className="text-red-500">*</span></label>
                  <CustomDropdown options={genderOptions} value={gender} onChange={(val) => { setGender(val); setErrors(p => ({ ...p, gender: undefined })); }} placeholder="Select gender" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.gender} />
                  {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                </div>
                <div>
                  <label className={labelClass}>Marital Status</label>
                  <CustomDropdown options={maritalStatusOptions} value={maritalStatus} onChange={setMaritalStatus} placeholder="Select marital status" isWhiteLabelingEnable={isWhiteLabelingEnable} />
                </div>
                <div>
                  <label className={labelClass}>Languages Known <span className="text-red-500">*</span></label>
                  <MultiSelectDropdown options={languages} value={languagesKnown} onChange={(val) => { setLanguagesKnown(val); setErrors(p => ({ ...p, languagesKnown: undefined })); }} placeholder="Select languages" searchPlaceholder="Search languages..." isWhiteLabelingEnable={isWhiteLabelingEnable} maxSelection={4} error={!!errors.languagesKnown} />
                  {errors.languagesKnown && <p className="text-red-500 text-xs mt-1">{errors.languagesKnown}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>No. of Years in College</label>
                  <CustomDropdown options={collegeYearsOptions} value={collegeYears} onChange={setCollegeYears} placeholder="Select years" isWhiteLabelingEnable={isWhiteLabelingEnable} />
                </div>
                <div>
                  <label className={labelClass}>Visa Status <span className="text-red-500">*</span></label>
                  <CustomDropdown options={visaStatusOptions} value={visaStatus} onChange={(val) => { setVisaStatus(val); setErrors(p => ({ ...p, visaStatus: undefined })); }} placeholder="Select visa status" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.visaStatus} />
                  {errors.visaStatus && <p className="text-red-500 text-xs mt-1">{errors.visaStatus}</p>}
                  {visaStatus === "Other" && (
                    <input type="text" value={otherVisaDetails} onChange={(e) => setOtherVisaDetails(e.target.value)} className={`mt-2 ${inputClass("otherVisaDetails")}`} placeholder="Please specify your visa status" maxLength={150} />
                  )}
                </div>
                <div>
                  <label className={labelClass}>Current Benefits</label>
                  <MultiSelectDropdown options={benefitsOptions} value={currentBenefits.join(", ")} onChange={(val) => setCurrentBenefits(val ? val.split(", ").filter(Boolean) : [])} placeholder="Select current benefits" searchPlaceholder="Search benefits..." isWhiteLabelingEnable={isWhiteLabelingEnable} />
                </div>
              </div>

              {/* Relatives */}
              <div>
                <label className={labelClass}>Relatives in Company</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" value={relativeName} onChange={(e) => setRelativeName(e.target.value)} className={inputClass("")} placeholder="Relative's name" />
                  <input type="text" value={relativeDesignation} onChange={(e) => setRelativeDesignation(e.target.value)} className={inputClass("")} placeholder="Relative's designation" />
                </div>
              </div>

              {/* Source */}
              <div>
                <label className={labelClass}>How did you hear about us? <span className="text-red-500">*</span></label>
                <CustomDropdown options={sourceOptions} value={sourceOfApplication} onChange={(val) => { setSourceOfApplication(val); setErrors(p => ({ ...p, sourceOfApplication: undefined })); }} placeholder="Select source" isWhiteLabelingEnable={isWhiteLabelingEnable} error={!!errors.sourceOfApplication} />
                {errors.sourceOfApplication && <p className="text-red-500 text-xs mt-1">{errors.sourceOfApplication}</p>}
                {sourceOfApplication === "Other" && (
                  <input type="text" value={otherSourceDetails} onChange={(e) => { setOtherSourceDetails(e.target.value); setErrors(p => ({ ...p, otherSourceDetails: undefined })); }} className={`mt-2 ${inputClass("otherSourceDetails")}`} placeholder="Please specify" maxLength={200} />
                )}
                {sourceOfApplication === "Employee Referral" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div>
                      <input type="text" value={referralName} onChange={(e) => { setReferralName(e.target.value); setErrors(p => ({ ...p, referralName: undefined })); }} className={inputClass("referralName")} placeholder="Employee Name" />
                      {errors.referralName && <p className="text-red-500 text-xs mt-1">{errors.referralName}</p>}
                    </div>
                    <div>
                      <input type="text" value={referralDesignation} onChange={(e) => { setReferralDesignation(e.target.value); setErrors(p => ({ ...p, referralDesignation: undefined })); }} className={inputClass("referralDesignation")} placeholder="Employee Designation" />
                      {errors.referralDesignation && <p className="text-red-500 text-xs mt-1">{errors.referralDesignation}</p>}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-2">
                <button type="button" onClick={prevStep} className="border-btn !text-gray-600">Back</button>
                <button type="button" onClick={handleSubmit} disabled={isSubmitting} className={`button ${isSubmitting ? "opacity-80 cursor-not-allowed" : ""}`}>
                  {isSubmitting ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Saving...</span>
                  ) : "Save & Continue"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}